// GroupsExplore Component - Discover and search groups

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Plus, Filter, Users, X } from 'lucide-react';
import { GroupCard } from './GroupCard';
import { searchGroups, getPopularGroups } from '../../services/groupsService';
import type { Group, GroupCategory, GROUP_CATEGORIES } from '../../types/groups';
import { useAuth } from '../../context/AuthContext';

interface GroupsExploreProps {
  onBack: () => void;
  onViewGroup: (groupId: string) => void;
  onCreateGroup: () => void;
  onViewMyGroups: () => void;
}

const CATEGORY_OPTIONS: { value: GroupCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'social', label: 'Gesellig' },
  { value: 'hobby', label: 'Hobby' },
  { value: 'neighborhood', label: 'Nachbarschaft' },
  { value: 'food_drink', label: 'Essen & Trinken' },
  { value: 'culture', label: 'Kultur' },
  { value: 'learning', label: 'Lernen' },
  { value: 'sports', label: 'Sport' },
  { value: 'nature', label: 'Natur' },
];

export function GroupsExplore({ onBack, onViewGroup, onCreateGroup, onViewMyGroups }: GroupsExploreProps) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<GroupCategory | 'all'>('all');
  const [groups, setGroups] = useState<Group[]>([]);
  const [popularGroups, setPopularGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Load popular groups on mount
  useEffect(() => {
    const loadPopular = async () => {
      try {
        const popular = await getPopularGroups(12);
        setPopularGroups(popular);
      } catch (err) {
        console.error('Error loading popular groups:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPopular();
  }, []);

  // Search when query or category changes
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (!searchQuery && selectedCategory === 'all') {
        setGroups([]);
        return;
      }

      setSearching(true);
      try {
        const results = await searchGroups({
          query: searchQuery || undefined,
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
        });
        setGroups(results);
      } catch (err) {
        console.error('Error searching groups:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [searchQuery, selectedCategory]);

  const displayGroups = (searchQuery || selectedCategory !== 'all') ? groups : popularGroups;
  const isSearching = searchQuery || selectedCategory !== 'all';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Gruppen entdecken</h1>
            <div className="flex-1" />
            {user && (
              <>
                <button
                  onClick={onViewMyGroups}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Meine Gruppen
                </button>
                <button
                  onClick={onCreateGroup}
                  className="p-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Search Bar */}
          <div className="mt-4 flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Gruppen suchen..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 border rounded-lg transition-colors ${
                showFilters || selectedCategory !== 'all'
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>

          {/* Category Filter */}
          {showFilters && (
            <div className="mt-3 flex flex-wrap gap-2">
              {CATEGORY_OPTIONS.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedCategory === cat.value
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {isSearching ? 'Suchergebnisse' : 'Beliebte Gruppen'}
              </h2>
              {isSearching && searching && (
                <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              )}
            </div>

            {/* Groups Grid */}
            {displayGroups.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {displayGroups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    onClick={() => onViewGroup(group.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {isSearching ? 'Keine Gruppen gefunden' : 'Noch keine Gruppen'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {isSearching
                    ? 'Versuche eine andere Suche oder erstelle selbst eine Gruppe'
                    : 'Sei der Erste und gruende eine Gruppe'}
                </p>
                {user && (
                  <button
                    onClick={onCreateGroup}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Gruppe erstellen
                  </button>
                )}
              </div>
            )}

            {/* CTA for logged out users */}
            {!user && (
              <div className="mt-8 p-6 bg-indigo-50 rounded-xl border border-indigo-100 text-center">
                <h3 className="text-lg font-semibold text-indigo-900 mb-2">
                  Werde Teil der Community!
                </h3>
                <p className="text-indigo-700 mb-4">
                  Melde dich an, um Gruppen beizutreten oder deine eigene zu gruenden.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default GroupsExplore;
